import React, { useState } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    address: '',
    preferred_date: '',
    project_details: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (value) => {
    setFormData({ ...formData, service_type: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quote`, formData);
      toast.success('Quote request submitted! We\'ll contact you within 24 hours.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_type: '',
        address: '',
        preferred_date: '',
        project_details: '',
      });
      onClose();
    } catch (error) {
      console.error('Quote form error:', error);
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50"
            data-testid="quote-modal-backdrop"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-4 md:inset-8 lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:max-w-2xl lg:w-full bg-white z-50 overflow-y-auto max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)]"
            data-testid="quote-modal"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase">
                  Request a Quote
                </h2>
                <p className="text-slate-600 text-sm">
                  Fill out the form and we'll get back to you within 24 hours.
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 transition-colors"
                data-testid="quote-modal-close"
              >
                <X className="h-6 w-6 text-slate-500" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="quote-name" className="block text-sm font-semibold text-slate-700 mb-2">
                    Name *
                  </label>
                  <Input
                    id="quote-name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    data-testid="quote-name-input"
                    className="rounded-none border-slate-300 focus:border-orange-500 focus:ring-orange-500 h-12"
                  />
                </div>
                <div>
                  <label htmlFor="quote-email" className="block text-sm font-semibold text-slate-700 mb-2">
                    Email *
                  </label>
                  <Input
                    id="quote-email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    data-testid="quote-email-input"
                    className="rounded-none border-slate-300 focus:border-orange-500 focus:ring-orange-500 h-12"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="quote-phone" className="block text-sm font-semibold text-slate-700 mb-2">
                    Phone *
                  </label>
                  <Input
                    id="quote-phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(123) 456-7890"
                    data-testid="quote-phone-input"
                    className="rounded-none border-slate-300 focus:border-orange-500 focus:ring-orange-500 h-12"
                  />
                </div>
                <div>
                  <label htmlFor="quote-service" className="block text-sm font-semibold text-slate-700 mb-2">
                    Service Type *
                  </label>
                  <Select
                    value={formData.service_type}
                    onValueChange={handleServiceChange}
                    required
                  >
                    <SelectTrigger 
                      className="rounded-none border-slate-300 focus:border-orange-500 focus:ring-orange-500 h-12"
                      data-testid="quote-service-select"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="concrete-cutting">Concrete Cutting</SelectItem>
                      <SelectItem value="core-drilling">Core Drilling</SelectItem>
                      <SelectItem value="demolition">Demolition</SelectItem>
                      <SelectItem value="multiple">Multiple Services</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="quote-address" className="block text-sm font-semibold text-slate-700 mb-2">
                    Project Address
                  </label>
                  <Input
                    id="quote-address"
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Main St, City, FL"
                    data-testid="quote-address-input"
                    className="rounded-none border-slate-300 focus:border-orange-500 focus:ring-orange-500 h-12"
                  />
                </div>
                <div>
                  <label htmlFor="quote-date" className="block text-sm font-semibold text-slate-700 mb-2">
                    Preferred Date
                  </label>
                  <Input
                    id="quote-date"
                    name="preferred_date"
                    type="date"
                    value={formData.preferred_date}
                    onChange={handleChange}
                    data-testid="quote-date-input"
                    className="rounded-none border-slate-300 focus:border-orange-500 focus:ring-orange-500 h-12"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="quote-details" className="block text-sm font-semibold text-slate-700 mb-2">
                  Project Details *
                </label>
                <Textarea
                  id="quote-details"
                  name="project_details"
                  required
                  rows={3}
                  value={formData.project_details}
                  onChange={handleChange}
                  placeholder="Describe your project..."
                  data-testid="quote-details-input"
                  className="rounded-none border-slate-300 focus:border-orange-500 focus:ring-orange-500 resize-none"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4 pb-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="sm:flex-1 bg-white text-slate-900 border-2 border-slate-300 hover:bg-slate-50 rounded-none px-6 py-4 font-bold uppercase tracking-wider"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="quote-submit-btn"
                  className="sm:flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-none px-6 py-4 font-bold uppercase tracking-wider transition-all hover:shadow-[4px_4px_0px_0px_rgba(51,65,85,1)] disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Request
                      <Send className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default QuoteModal;
